import fetch from 'app/utils/requests/fetch';
import type { ResourceCtaActionContext } from 'app/features/resources/cta/actions';
import type {
  LeadsListParams,
  LeadsListResult,
  LeadStatus,
  LeadSummary,
} from 'app/features/leads/services/gym-finder/types';
import { formatLeadInterests } from 'app/features/leads/utils/format-lead-interest';
import { getPersistedState } from 'state/auth';

type CreateHyrox365ServiceArgs = {
  apiUrl: string;
};

type GymUpdateLinkResponse = {
  redirectUrl?: string;
  url?: string;
};

type Hyrox365Lead = {
  leadId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  interests: string[];
  events: string[];
  notes: string;
  status: LeadStatus;
  requestedAt: string;
  updatedAt: string;
};

type Hyrox365LeadsResponse = {
  leads?: Hyrox365Lead[];
  nextCursor?: string;
};

type Hyrox365LeadResponse = {
  lead: Hyrox365Lead;
};

const getApiRoot = (apiUrl: string) => apiUrl.replace(/\/graphql\/?$/, '').replace(/\/$/, '');

const isValidRedirectUrl = (url: string): boolean => {
  try {
    return ['http:', 'https:'].includes(new URL(url).protocol);
  } catch {
    return false;
  }
};

const getAuthHeaders = () => {
  const { authorization } = getPersistedState();

  return {
    ...(authorization ? { Authorization: authorization } : {}),
    'content-type': 'application/json',
  };
};

const createLeadsQueryString = ({ limit, cursor, name, email, status, interest }: LeadsListParams): string => {
  const params = new URLSearchParams();
  if (limit) {
    params.set('limit', String(limit));
  }
  if (cursor) {
    params.set('cursor', cursor);
  }
  if (name?.trim()) {
    params.set('name', name.trim());
  }
  if (email?.trim()) {
    params.set('email', email.trim());
  }
  if (status) {
    params.set('status', status);
  }
  if (interest?.trim()) {
    params.set('interest', interest.trim());
  }
  const serialised = params.toString();
  return serialised ? `?${serialised}` : '';
};

// Upstream is a third party, so individual fields can be missing or malformed.
// Coerce everything into the shape the UI expects so a single bad lead can't
// crash the whole table.
const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

const mapLead = (lead: Hyrox365Lead): LeadSummary => {
  const interests = asStringArray(lead?.interests);
  const events = asStringArray(lead?.events);
  const interestedIn = formatLeadInterests(interests);

  return {
    id: asString(lead?.leadId),
    athleteName: [asString(lead?.firstName), asString(lead?.lastName)].filter(Boolean).join(' '),
    athleteEmail: asString(lead?.email),
    athletePhone: asString(lead?.phone),
    enquiryType: interestedIn || events.join(', '),
    status: lead?.status,
    receivedAt: asString(lead?.requestedAt),
    updatedAt: asString(lead?.updatedAt),
    signedUpForHyrox: events.length > 0,
    interestedIn,
    message: asString(lead?.notes),
  };
};

const createHyrox365Service = ({ apiUrl }: CreateHyrox365ServiceArgs) => {
  const createGymUpdateLink = async ({ targetWindow }: ResourceCtaActionContext): Promise<void> => {
    const { body } = await fetch(`${getApiRoot(apiUrl)}/hyrox365/v1/gyms/me/update-link`, {
      headers: getAuthHeaders(),
      method: 'POST',
    });

    const { redirectUrl, url } = body as GymUpdateLinkResponse;
    const resolvedUrl = redirectUrl || url;

    if (!resolvedUrl || !isValidRedirectUrl(resolvedUrl)) {
      throw new Error('Hyrox365 update link response did not include a redirect URL');
    }

    if (!targetWindow) {
      throw new Error('Hyrox365 update link target window was not available');
    }

    targetWindow.opener = null;
    targetWindow.location.href = resolvedUrl;
  };

  const listLeads = async (params: LeadsListParams): Promise<LeadsListResult> => {
    const { body } = await fetch(
      `${getApiRoot(apiUrl)}/hyrox365/v1/fiit/gyms/me/leads${createLeadsQueryString(params)}`,
      {
        headers: getAuthHeaders(),
        method: 'GET',
      },
    );

    const { leads = [], nextCursor } = body as Hyrox365LeadsResponse;
    const mappedLeads = leads.map(mapLead);

    return {
      leads: mappedLeads,
      totalCount: mappedLeads.length,
      profileComplete: true,
      nextCursor,
    };
  };

  const getLead = async (id: string): Promise<LeadSummary> => {
    const { body } = await fetch(`${getApiRoot(apiUrl)}/hyrox365/v1/fiit/leads/${encodeURIComponent(id)}`, {
      headers: getAuthHeaders(),
      method: 'GET',
    });

    const { lead } = body as Hyrox365LeadResponse;
    return mapLead(lead);
  };

  const updateLeadStatus = async (id: string, status: LeadStatus): Promise<LeadSummary> => {
    const { body } = await fetch(`${getApiRoot(apiUrl)}/hyrox365/v1/fiit/leads/${encodeURIComponent(id)}`, {
      body: JSON.stringify({ status }),
      headers: getAuthHeaders(),
      method: 'PATCH',
    });

    const { lead } = body as Hyrox365LeadResponse;
    return mapLead(lead);
  };

  return {
    createGymUpdateLink,
    getLead,
    listLeads,
    updateLeadStatus,
  };
};

export type Hyrox365Service = ReturnType<typeof createHyrox365Service>;

export default createHyrox365Service;
